import React from 'react';
import { Link } from 'react-router-dom';
import navSections, { visibleNavSections } from '../SysadminSPA/shared/navSections';
import type { NavAccessFlags, NavLeaf } from '../SysadminSPA/shared/navSections';
import {
  childrenClass, railItemClass, sectionLabelClass, subItemClass,
} from '../SysadminSPA/shared/railClasses';

// The SPA's routed pages live under /sysadmin; from a legacy page they are a full load like the rest.
const SPA_BASE = '/sysadmin';

export const legacyHrefFor = (item: NavLeaf) => item.legacyHref ?? item.href ?? `${SPA_BASE}${item.to}`;

const matches = (path: string, href: string) => path === href || path.startsWith(`${href}/`);

// The item whose legacy page this is: the longest matching prefix, so /admin/programs/alert-messages
// lights Alert Messages and not Programs as well.
export const activeHref = (path: string, items: NavLeaf[]) => items
  .map(item => item.legacyHref)
  .filter((href): href is string => Boolean(href) && matches(path, href as string))
  .sort((a, b) => b.length - a.length)[0];

// The legacy content view is the Content and Support tree; Dev and Elsewhere are SPA-only. The
// admin_view value is what a full load of the item sets the legacy header's view cookie to
// (ApplicationController#check_admin_view), so the old chrome follows the rail.
const LEGACY_SECTIONS: Record<string, string> = { Content: 'content', Support: 'support' };

export const withAdminView = (href: string, view: string) => {
  const url = new URL(href, window.location.origin);
  if (url.origin !== window.location.origin) return href;
  if (!url.searchParams.has('admin_view')) url.searchParams.set('admin_view', view);

  return `${url.pathname}${url.search}`;
};

interface LegacyNavTreeProps extends NavAccessFlags {
  currentPath: string;
  // A router path for items that are tabs of the page hosting the rail; those render as router
  // links (client-side) instead of anchors. Only pass this from inside a router.
  linkTo?: (item: NavLeaf) => string | null;
  supportPath: string;
}

// AIDEV-NOTE: The SPA rail's at-rest app tree (SPANav) rendered for the legacy content rail when
// there is no program to drill: anchors, every destination a full page load — except the hosting
// page's own tabs when it mounts the rail inside its router (admin/ProgramsPage).
const LegacyNavTree = ({
  canManageContent, canManageSupport, currentPath, linkTo, supportPath,
}: LegacyNavTreeProps) => {
  const sections = visibleNavSections(navSections(supportPath), { canManageContent, canManageSupport })
    .filter(section => Boolean(LEGACY_SECTIONS[section.label]));
  const current = activeHref(currentPath, sections.flatMap(section => section.items));

  return (
    <nav aria-label="Sysadmin navigation" className="tw-p-2">
      {sections.map(section => (
        <div key={section.label}>
          <div className={sectionLabelClass}>{section.label}</div>
          {section.items.map((item) => {
            const href = withAdminView(legacyHrefFor(item), LEGACY_SECTIONS[section.label]);
            const active = legacyHrefFor(item) === current;
            const className = item.sub ? subItemClass(active) : railItemClass(active);
            const to = linkTo?.(item);
            const link = to ? (
              <Link aria-current={active ? 'page' : undefined} className={className} to={to}>{item.label}</Link>
            ) : (
              <a aria-current={active ? 'page' : undefined} className={className} href={href}>{item.label}</a>
            );

            return item.sub
              ? <div key={item.label} className={childrenClass}>{link}</div>
              : <React.Fragment key={item.label}>{link}</React.Fragment>;
          })}
        </div>
      ))}
    </nav>
  );
};

export default LegacyNavTree;
